import axe from './axiosClient';
import { quotationsBackofficeAxe } from './quotationsBackofficeAxeClient';
import { backOfficeAxe } from './backofficeClient';
import quotationsAxe from './quotationsAxeClient';
// Named export here, unlike greenia-frontend's chatAxeClient which default-exports it.
import { chatAxe } from './chatAxeClient';

/* The shared client sets validateStatus to `status < 500`, so 403/404 resolve into .then
   with no `data.data` payload. Unguarded, a 404 reads as an empty document list — which
   would tell a broker a garanzia needs no documents when it needs thirteen. Every reader
   below goes through unwrap so that failure mode cannot come back. */
const unwrap = (res) => {
  if (res.status !== 200 || !res.data || !res.data.data) {
    const error = new Error(`Unexpected response ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.data.data;
};

export const getCauzioniCatalogue = () => axe
  .get('/surety-bonds/catalogue')
  .then(unwrap);

/* One call for every rischio's documents, grouped by rischio. Replaces the per-rischio
   fan-out — Generica Enti Pubblici alone fired 26 concurrent requests on a single click. */
export const getCauzioniSubProductDocuments = (subProductId) => axe
  .get(`/surety-bonds/sub-products/${subProductId}/documents`)
  .then(unwrap);

export const getCauzioniRiskDocuments = (riskId) => axe
  .get(`/surety-bonds/risks/${riskId}/documents`)
  .then(unwrap);

export const searchCauzioniCustomers = (gender, q) => axe
  .get(`/surety-bonds/customers?gender=${encodeURIComponent(gender)}&q=${encodeURIComponent(q)}`)
  .then(unwrap);

export const getCauzioniRequest = (requestToken) => axe
  .get(`/surety-bonds/requests/${requestToken}`)
  .then(unwrap);

/* Submission lives in the quotation-service, not greenia-back, so it goes through
   quotationsAxe rather than axe/unwrap: this endpoint returns { request_token } at the
   top level, not the { data: { ... } } envelope every other cauzioni reader above expects.
   The caller checks res.status and res.data.request_token itself. */
export const submitCauzioniRequest = (payload) => quotationsAxe
  .post('/quotations/surety-bonds/quote', payload);

/* Backoffice "Modifica Dati": posts to the re-quote endpoint with the ORIGINAL token, which
   creates a new request attributed to that request's broker. Mirrors how every other product's
   backoffice edit saves (formsBackofficeActions.js condominioSubmitAnswers). */
export const submitCauzioniRequestBackoffice = (requestToken, payload) => quotationsBackofficeAxe
  .post(`/quotations/surety-bonds/quote/${requestToken}`, payload);

/* Backoffice-token twins of the reads the request wizard performs. The wizard is shared between
   the broker flow and the backoffice "Modifica Dati" flow, and a backoffice session carries no
   broker token — calling the broker endpoints from there answers 401 and the step renders empty.
   Same handlers on the server, mounted under the back-office route group. */
export const getCauzioniRiskDocumentsBackoffice = (riskId) => backOfficeAxe
  .get(`/surety-bonds/risks/${riskId}/documents`).then(unwrap);

export const getCauzioniRequestBackoffice = (requestToken) => backOfficeAxe
  .get(`/surety-bonds/requests/${requestToken}`).then(unwrap);

export const uploadDocumentBackoffice = (data) => backOfficeAxe.post('/upload-document', data);

/* Reads a bando, disciplinare or richiesta di anticipazione and answers which garanzie it calls
   for. Lives in greenia-chat-service because that is where the OpenAI key and the LLM plumbing
   are — hence chatAxe rather than the greenia-back client.

   The document is not stored anywhere: it is parsed to text, matched against the catalogue, and
   dropped. Analysis takes ~5s, so this deliberately overrides the default timeout. */
export const suggestCauzioniFromDocument = (base64Document, filename) => chatAxe
  .post(
    '/cauzioni-ai/suggest-from-document',
    { document: base64Document, filename },
    { timeout: 90000 },
  );
